import React, { useMemo, useState, useRef, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight, Search, Plus } from "lucide-react";
import ItemModal from "../components/ItemModal";
import ClosedNoticeModal from "../components/ClosedNoticeModal";
import { useStoreClosure } from "../context/StoreClosureContext";
import { useApi } from "../utils/Api";
import { Helmet } from "react-helmet-async";
import DSC1641 from "../assets/DSC_1641.jpg";
import DSC1668 from "../assets/DSC_1668.jpg";
import DSC1682 from "../assets/DSC_1682.jpg";
import DSC1698 from "../assets/DSC_1698.jpg";
import DSC1716 from "../assets/DSC_1716.jpg";
import DSC1811 from "../assets/DSC_1811.jpg";
import DSC1820 from "../assets/DSC_1820.jpg";
import DSC1841 from "../assets/DSC_1841.jpg";

const heroImages = [DSC1641, DSC1668, DSC1682, DSC1698, DSC1716, DSC1811, DSC1820, DSC1841];


const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

export default function MenuPage() {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeCategory, setActiveCategory] = useState("");
  const [search, setSearch] = useState("");

  const { isClosed, closedReason } = useStoreClosure();

  const categoryRefs = useRef({});
  const categoryScrollRef = useRef(null);

  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [emblaRef] = useEmblaCarousel(
    { loop: true, slidesToScroll: 1 },
    [Autoplay({ delay: 3000, stopOnInteraction: false })]
  );

  const { apiFetch } = useApi();

  const formatMoney = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(Number(n || 0));

  const fetchCategories = React.useCallback(async () => {
    const data = await apiFetch("categories");
    setCategories(Array.isArray(data) ? data : []);
  }, [apiFetch]);

  const fetchMenuItems = React.useCallback(async () => {
    const data = await apiFetch("menu-items");
    setMenuItems(Array.isArray(data) ? data : []);
  }, [apiFetch]);

  // Initial load (wait for both calls)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");

      try {
        await Promise.all([fetchCategories(), fetchMenuItems()]);
      } catch (err) {
        console.error("Menu load error:", err);
        if (!cancelled) {
          setCategories([]);
          setMenuItems([]);
          setError("Failed to load menu. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchCategories, fetchMenuItems]);

  const menu = useMemo(() => {
    if (!categories.length) return [];

    return categories.map((cat) => {
      // Items directly in this category
      const directItems = menuItems
        .filter((item) => Number(item.category_id) === Number(cat.id))
        .map((item) => ({
          id: item.id,
          name: item.item_name,
          description: item.item_description,
          price: Number(item.price),
          image: item.image,
          in_stock: item.in_stock,
          required_options: item.required_options || [],
          optional_options: item.optional_options || [],
        }));

      // Items in subcategories
      const subItems =
        cat.subcategories?.flatMap((sub) =>
          menuItems
            .filter((item) => Number(item.category_id) === Number(sub.id))
            .map((item) => ({
              id: item.id,
              name: item.item_name,
              description: item.item_description,
              price: Number(item.price),
              image: item.image,
              in_stock: item.in_stock,
              subcategory: sub.category_name,
              required_options: item.required_options || [],
              optional_options: item.optional_options || [],
            })),
        ) || [];

      return {
        id: String(cat.id),
        title: cat.category_name,
        items: [...directItems, ...subItems],
      };
    });
  }, [categories, menuItems]);

  // Set default active category when menu loads
  useEffect(() => {
    if (menu.length > 0 && !activeCategory) {
      setActiveCategory(menu[0].id);
    }
  }, [menu, activeCategory]);

  // Filter menu by search
  const filteredMenu = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return menu;

    return menu
      .map((cat) => ({
        ...cat,
        items: cat.items.filter((item) =>
          item.name.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [menu, search]);

  // Track active category based on scroll
  useEffect(() => {
    if (loading) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;

      for (const cat of filteredMenu) {
        const ref = categoryRefs.current[cat.id];
        if (ref) {
          const { offsetTop, offsetHeight } = ref;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveCategory(cat.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [filteredMenu, loading]);

  // Scroll category nav to keep active tab visible
  useEffect(() => {
    if (loading) return;

    const container = categoryScrollRef.current;
    if (!container) return;

    const activeBtn = container.querySelector(`[data-cat="${activeCategory}"]`);
    if (!activeBtn) return;

    activeBtn.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeCategory, loading]);

  const scrollToCategory = (id) => {
    const ref = categoryRefs.current[id];
    if (!ref) return;

    const navHeight = categoryScrollRef.current?.offsetHeight || 0;
    const offsetTop = ref.offsetTop - navHeight - 90;

    window.scrollTo({
      top: offsetTop,
      behavior: "smooth",
    });
  };

  return (
    <div className="min-h-screen bg-[#f9f9f9] py-12 font-sans">
      <Helmet>
        <title>Menu | Dee Delicious Afro-Caribbean Restaurant</title>

        <meta
          name="description"
          content="Explore the Dee Delicious menu featuring authentic Nigerian cuisines and Jamaican dishes in Liverpool including jollof rice, suya, soups, and Caribbean favourites."
        />

        <link rel="canonical" href="https://deedelicious.com/menu" />

        <meta property="og:title" content="Menu | Dee Delicious Afro-Caribbean Restaurant, Liverpool" />
        <meta property="og:description" content="Discover Nigerian and Jamaican dishes freshly prepared at Dee Delicious." />
        <meta property="og:url" content="https://deedelicious.com/menu" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://deedelicious.com/static/media/logo.06d3da75fe2458d68075.png" />

        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Spinner */}
      {loading && <SpinnerOverlay />}

      {/* Closed-day notice modal (auto-shows on closed dates) */}
      <ClosedNoticeModal />

      <div className="mx-auto max-w-6xl px-6 flex flex-col gap-8">
        {/* Hero Header */}
        <div className="relative rounded-[2.5rem] overflow-hidden p-[2px] shadow-2xl"
          style={{ background: "linear-gradient(135deg, #c8a96e 0%, #3a2a1a 40%, #c8a96e 100%)" }}>
          {/* Inner frame */}
          <div className="relative bg-stone-950 rounded-[2.4rem] p-2 sm:p-3 md:p-5">
            {/* Embla Carousel viewport */}
            {/* Embla viewport — overflow hidden */}
            <div ref={emblaRef} className="overflow-hidden rounded-[1.8rem] h-[180px] sm:h-[210px] md:h-[230px] lg:h-[250px]">
              {/* Container: negative margin offsets the padding on the first slide */}
              <div className="flex h-full -ml-3">
                {heroImages.map((img, idx) => (
                  <div
                    key={idx}
                    className="
  flex-[0_0_100%]
  sm:flex-[0_0_55%]
  md:flex-[0_0_40%]
  lg:flex-[0_0_25%]
  xl:flex-[0_0_22%]
  min-w-0
  pl-3
"
                  >
                    {/* Inner box carries the visual styles */}
                    <div className="h-full rounded-xl overflow-hidden shadow-lg ring-1 ring-white/5">
                      <img
                        src={img}
                        alt={`Afro-Caribbean food ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* Inline closed-day banner */}
        {isClosed && (
          <div className="closed-day-banner">
            <span className="closed-day-banner-icon">🚫</span>
            <span>
              Ordering is unavailable today — we are closed
              {closedReason ? ` for ${closedReason}` : ""}.
            </span>
          </div>
        )}

        {/* Notice */}
        <div className="relative overflow-hidden rounded-2xl border-l-[6px] border-secondary bg-primary/5 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary text-white text-xs">
              ⚠
            </div>
            <p className="text-sm font-bold text-secondary tracking-wide uppercase">Notice</p>
          </div>
          <ul className="space-y-1 text-xs text-gray-700 leading-relaxed">
            <li>
              • Locally sourced ingredients & <span className="font-semibold text-stone-900">100% Halal Food</span>
            </li>
            <li>
              • If you have a food allergy, intolerance, or coeliac disease,
              please speak to our staff before ordering as our ingredients may
              contain peanuts, milk, wheat, dairy and poultry.
            </li>
          </ul>
        </div>

        {/* Search */}
        <div className="relative w-full group">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-primary transition-colors" size={20} />
          <input
            type="text"
            placeholder="Search meals..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-2xl border border-stone-200 bg-white pl-14 pr-5 py-4 text-sm shadow-sm focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all outline-none"
          />
        </div>

        {/* Category Nav */}
        <div className="sticky top-20 z-[50] bg-[#f3f3f3] p-4 flex items-center gap-2">
          <button
            className="block md:hidden p-2 rounded-full bg-black/5 hover:bg-black/10 transition text-gray-600"
            onClick={() =>
              categoryScrollRef.current?.scrollBy({
                left: -180,
                behavior: "smooth",
              })
            }
          >
            <ChevronLeft size={18} strokeWidth={2.2} />
          </button>

          <div
            ref={categoryScrollRef}
            className="category-scroll flex gap-3 overflow-x-auto flex-1"
          >
            {filteredMenu.map((cat) => {
              const activeState = cat.id === activeCategory;
              const displayTitle = cat.title === "Nigerian Soups" ? "Nigerian Soups & Swallow" : cat.title;

              return (
                <button
                  key={cat.id}
                  data-cat={cat.id}
                  onClick={() => scrollToCategory(cat.id)}
                  className={`flex items-center gap-2 whitespace-nowrap px-5 py-3 rounded-xl font-semibold transition-all duration-200 ${activeState
                    ? "bg-orange-500 text-white shadow-lg shadow-orange-500/20"
                    : "bg-white text-stone-600 hover:bg-orange-50"
                    }`}
                >
                  {displayTitle}
                </button>
              );
            })}
          </div>

          <button
            className="block md:hidden p-2 rounded-full bg-black/5 hover:bg-black/10 transition text-gray-600"
            onClick={() =>
              categoryScrollRef.current?.scrollBy({
                left: 180,
                behavior: "smooth",
              })
            }
          >
            <ChevronRight size={18} strokeWidth={2.2} />
          </button>
        </div>

        {/* Error */}
        {!loading && error && (
          <div className="text-center text-red-500 py-10">{error}</div>
        )}

        {/* Only show “No items found” after loading is finished */}
        {!loading && !error && filteredMenu.length === 0 && (
          <div className="text-center text-stone-500 py-16">No items found.</div>
        )}

        {/* Items */}
        {!loading &&
          !error &&
          filteredMenu.map((cat) => (
            <div
              key={cat.id}
              ref={(el) => (categoryRefs.current[cat.id] = el)}
              className="pt-6"
            >
              <h2 className="text-2xl font-black text-stone-900 mb-5 tracking-tight border-l-4 border-primary pl-3">
                {cat.title === "Nigerian Soups" ? "Nigerian Soups & Swallow" : cat.title}
              </h2>

              <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {cat.items.map((item) => (
                  <div
                    key={item.id}
                    className={`group relative rounded-[2rem] overflow-hidden shadow-sm transition-all duration-300 flex flex-col bg-[#f6f6f6] ${item.in_stock
                      ? "hover:shadow-md cursor-pointer"
                      : "cursor-not-allowed"
                      }`}
                    onClick={() => {
                      if (!item.in_stock || isClosed) return;
                      setSelectedItem(item);
                      setModalOpen(true);
                    }}
                  >
                    <div className="relative overflow-hidden w-full h-56">
                      {item.image ? (
                        <img
                          src={`https://deedelicious.com/${item.image}`}
                          alt={item.name}
                          className={`w-full h-full object-cover transition-transform duration-500 ease-out ${item.in_stock ? "group-hover:scale-105" : ""
                            }`}
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-stone-100 flex items-center justify-center text-stone-400">
                          No Image Available
                        </div>
                      )}

                      {item.subcategory && (
                        <div className="absolute top-4 left-4 z-10 bg-white/95 backdrop-blur-md text-stone-800 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm border border-stone-100">
                          {item.subcategory}
                        </div>
                      )}
                    </div>

                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex-1">
                        <div className="flex flex-col min-[320px]:flex-row min-[320px]:justify-between min-[320px]:items-start gap-1 min-[320px]:gap-3">
                          <h3 className="text-lg font-bold leading-snug text-stone-900 break-words">
                            {item.name}
                          </h3>

                          <p className="text-lg font-extrabold whitespace-nowrap text-primary">
                            {formatMoney(item.price)}
                          </p>
                        </div>

                        <p className="mt-3 text-sm leading-relaxed line-clamp-3 text-stone-500">
                          {item.description}
                        </p>
                      </div>

                      <div className="mt-6">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!item.in_stock || isClosed) return;
                            setSelectedItem(item);
                            setModalOpen(true);
                          }}
                          className={`w-full flex items-center justify-center gap-2 rounded-full py-3 text-md font-semibold transition-all duration-200 ${
                            isClosed
                              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                              : "bg-[#FFF0EA] text-primary hover:bg-primary hover:text-white active:scale-[0.98]"
                          }`}
                          disabled={isClosed}
                        >
                          <Plus size={16} strokeWidth={3} />
                          <span>{isClosed ? "Ordering Unavailable" : item.in_stock ? "Add to Order" : "Unavailable"}</span>
                        </button>
                      </div>
                    </div>

                    {!item.in_stock && (
                      <div
                        className="absolute inset-0 z-10 flex items-center justify-center text-white text-xl font-bold tracking-wide"
                        style={{ backgroundColor: "rgba(0, 0, 0, 0.45)" }}
                      >
                        Out of Stock
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

        {/* Modal */}
        <ItemModal
          item={selectedItem}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      </div>
    </div>
  );
}
